import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';

declare global {
  interface Window {
    google: any;
    initGoogleMapsDisplay: () => void;
  }
}

interface GoogleMapsDisplayProps {
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  title?: string;
  height?: number;
  zoom?: number;
  showMarker?: boolean;
  interactive?: boolean;
}

export const GoogleMapsDisplay: React.FC<GoogleMapsDisplayProps> = ({
  latitude,
  longitude,
  address,
  city,
  title = 'Property Location',
  height = 300,
  zoom = 15,
  showMarker = true,
  interactive = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const addAreaMarker = useCallback((lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add a general area circle instead of precise marker
    markerRef.current = new window.google.maps.Circle({
      center: { lat, lng },
      radius: 2000, // 2km radius for general area
      map: mapInstanceRef.current,
      fillColor: '#1976d2',
      fillOpacity: 0.2,
      strokeColor: '#1976d2',
      strokeOpacity: 0.6,
      strokeWeight: 2,
    });

    // Add info window on click
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 8px; max-width: 200px;">
          <h4 style="margin: 0 0 4px 0; font-size: 14px; font-weight: bold;">${title}</h4>
          <p style="margin: 0; font-size: 12px; color: #666;">General area - exact location provided after booking</p>
          ${address ? `<p style="margin: 4px 0 0 0; font-size: 12px; color: #666;">${address}</p>` : ''}
        </div>
      `,
      position: { lat, lng }
    });

    mapInstanceRef.current.addListener('click', (event: any) => {
      const clickLat = event.latLng.lat();
      const clickLng = event.latLng.lng();
      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(lat, lng),
        new window.google.maps.LatLng(clickLat, clickLng)
      );
      
      if (distance <= 2000) { // Within the circle
        infoWindow.open(mapInstanceRef.current);
      }
    });
  }, [title, address]);

  const addMarker = useCallback((lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      title: title,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="12" fill="#1976d2" stroke="white" stroke-width="2"/>
            <circle cx="16" cy="16" r="4" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      }
    });

    // Add info window on marker click
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 8px; max-width: 200px;">
          <h4 style="margin: 0 0 4px 0; font-size: 14px; font-weight: bold;">${title}</h4>
          ${address ? `<p style="margin: 0; font-size: 12px; color: #666;">${address}</p>` : ''}
        </div>
      `
    });

    markerRef.current.addListener('click', () => {
      infoWindow.open(mapInstanceRef.current, markerRef.current);
    });
  }, [title, address]);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    try {
      // Default center (San Francisco if no coordinates provided)
      const defaultCenter = { lat: 37.7749, lng: -122.4194 };
      const center = latitude && longitude 
        ? { lat: latitude, lng: longitude }
        : defaultCenter;

      // Initialize map
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: latitude && longitude ? zoom : 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: interactive,
        zoomControl: interactive,
        scrollwheel: interactive,
        disableDoubleClickZoom: !interactive,
        draggable: interactive,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Add marker if coordinates are provided
      if (latitude && longitude && showMarker) {
        addMarker(latitude, longitude);
      }

      setIsMapReady(true);
      setApiError(null);
    } catch (error) {
      console.error('Error initializing map:', error);
      setApiError('Failed to initialize map');
    }
  }, [latitude, longitude, zoom, interactive, showMarker, addMarker]);

  const loadGoogleMapsAPI = useCallback(async () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setApiError('Google Maps API key not configured');
      return;
    }

    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    setIsLoading(true);
    
    try {
      // Create callback function
      window.initGoogleMapsDisplay = () => {
        initializeMap();
        setIsLoading(false);
      };

      // Check if script already exists
      if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry&callback=initGoogleMapsDisplay`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          setApiError('Failed to load Google Maps API');
          setIsLoading(false);
        };
        document.head.appendChild(script);
      } else {
        // Script exists, just initialize
        initializeMap();
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading Google Maps API:', error);
      setApiError('Failed to load Google Maps API');
      setIsLoading(false);
    }
  }, [initializeMap]);

  const updateMapLocation = useCallback((lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;

    const newCenter = { lat, lng };
    mapInstanceRef.current.setCenter(newCenter);
    mapInstanceRef.current.setZoom(zoom);

    if (showMarker) {
      addMarker(lat, lng);
    }
  }, [zoom, showMarker, addMarker]);

  const geocodeCity = useCallback(async (cityName: string) => {
    if (!window.google || !mapInstanceRef.current) return;

    const geocoder = new window.google.maps.Geocoder();
    
    try {
      geocoder.geocode({ address: cityName }, (results: any[], status: any) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(12); // City-level zoom
          
          // Add a general area marker if showMarker is true
          if (showMarker) {
            addAreaMarker(lat, lng);
          }
        } else {
          console.warn('Geocoding failed:', status);
        }
      });
    } catch (error) {
      console.error('Error geocoding city:', error);
    }
  }, [showMarker, addAreaMarker]);

  // Load Google Maps API
  useEffect(() => {
    loadGoogleMapsAPI();
  }, [loadGoogleMapsAPI]);

  // Update map when coordinates change or geocode city
  useEffect(() => {
    if (isMapReady) {
      if (latitude && longitude) {
        updateMapLocation(latitude, longitude);
      } else if (city) {
        geocodeCity(city);
      }
    }
  }, [latitude, longitude, city, isMapReady, geocodeCity, updateMapLocation]);

  // Fallback display when no coordinates and no city
  if (!latitude && !longitude && !city) {
    return (
      <Box
        sx={{
          height: height,
          backgroundColor: 'grey.100',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <LocationIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {address || 'Location information not available'}
        </Typography>
      </Box>
    );
  }

  if (apiError) {
    return (
      <Alert severity="error" sx={{ height: height, display: 'flex', alignItems: 'center' }}>
        {apiError}
      </Alert>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
            borderRadius: 2,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
      
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: height,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.100',
        }}
      />
    </Box>
  );
};

export default GoogleMapsDisplay;